import { useCallback, useMemo } from 'react';
import type { GenerationAsset } from '../state/types';
import type { RecommendedKeys } from '../../common/HierarchicalSelect';
import { useRecommendations, resolveRecommendationSubject } from './useRecommendations';
import { useRecommendationEvents } from './useRecommendationEvents';

/**
 * 商品AIプロファイル由来の属性推奨 (計画書§5.2 / §5.3 / §8 P4) をまとめたフック。
 *
 * GenerationWorkspace から切り出したもので、ロジックは移設のみ (挙動不変):
 * - 選択中素材から推奨の subject を解決し `useRecommendations` を張る
 * - AIおまかせボタンの状態 (gachaMode / gachaTooltip / gachaLabel)
 * - ドロップダウンのバッジ表示用 `recommendedKeysByGroup`
 * - 推奨採用ログを載せた `handleAttributeChange` (setAttribute の 1 箇所ラップ)
 */
export function useAttributeRecommendations({
  selectedAssets,
  setAttribute,
}: {
  selectedAssets: GenerationAsset[];
  setAttribute: (key: string, value: unknown) => void;
}) {
  // AIおまかせ（ガチャ置換。計画書§5.3）: 選択中素材の先頭1件から商品AIプロファイルの
  // 推奨を取得する。素材未選択時は subject が null になり無効化される。
  const recommendationSubject = useMemo(
    () => resolveRecommendationSubject(selectedAssets),
    [selectedAssets],
  );
  const recommendationsQuery = useRecommendations(recommendationSubject);
  // ボタンの状態: ready(AIおまかせ) / pending(解析中、無効化) / random(ランダム選択にフォールバック)
  const gachaMode = useMemo((): 'ready' | 'pending' | 'random' => {
    if (!recommendationSubject) return 'random';
    if (recommendationsQuery.isError) return 'random';
    const status = recommendationsQuery.data?.status;
    if (status === 'ready') return 'ready';
    // 明示的な 'pending' と初回ロード中 (data 未取得) だけ「解析中」。
    // pending が長引いた場合は「ランダム」に戻し、AIおまかせ補助の不調で操作不能にしない。
    // それ以外 (failed / unsupported / none / 想定外) は全てランダムに落とす。
    // サーバは DB 障害時に {status:'none'} を返すため、未知ステータス=ランダムが安全側。
    if (status === undefined || (status === 'pending' && !recommendationsQuery.isPendingExpired)) {
      return 'pending';
    }
    return 'random';
  }, [recommendationSubject, recommendationsQuery.data, recommendationsQuery.isError, recommendationsQuery.isPendingExpired]);
  const gachaTooltip = useMemo(() => {
    if (gachaMode === 'ready') return 'AIおまかせ（商品に合わせて自動選択）';
    if (gachaMode === 'pending') return '解析中…';
    if (recommendationsQuery.data?.status === 'pending' && recommendationsQuery.isPendingExpired) {
      return '解析に時間がかかっているためランダム選択になります';
    }
    if (recommendationsQuery.data?.status === 'failed') {
      return '商品の解析に失敗したためランダム選択になります';
    }
    return 'ランダム選択';
  }, [gachaMode, recommendationsQuery.data, recommendationsQuery.isPendingExpired]);
  const gachaLabel = gachaMode === 'ready' ? 'AIおまかせ' : gachaMode === 'pending' ? '解析中' : 'ランダム';

  // 属性ドロップダウンの推奨表示 (計画書§5.2)。グループ key (= entry.key。useGacha の
  // group_key と同じ「option の直近の親ノード key」) ごとに strong/suggested の value を束ねる。
  // ready 以外は undefined。並べ替え＋バッジのみで既定選択はしない。
  const recommendedKeysByGroup = useMemo((): Record<string, RecommendedKeys> | undefined => {
    const data = recommendationsQuery.data;
    if (!data || data.status !== 'ready') return undefined;
    const map: Record<string, RecommendedKeys> = {};
    for (const entry of data.strong) {
      (map[entry.key] ??= { strong: [], suggested: [] }).strong.push(entry.value);
    }
    for (const entry of data.suggested) {
      (map[entry.key] ??= { strong: [], suggested: [] }).suggested.push(entry.value);
    }
    return map;
  }, [recommendationsQuery.data]);

  // recommendedKeysByGroup と同じ走査だが、採用ログ (計画書§8 P4) 用に
  // strength/score を group key -> option value で引けるようにしたルックアップ。
  const recommendationEntryByGroupValue = useMemo((): Record<string, Record<string, { strength: 'strong' | 'suggested'; score?: number }>> => {
    const data = recommendationsQuery.data;
    const map: Record<string, Record<string, { strength: 'strong' | 'suggested'; score?: number }>> = {};
    if (!data || data.status !== 'ready') return map;
    for (const entry of data.strong) {
      (map[entry.key] ??= {})[entry.value] = { strength: 'strong', score: entry.score };
    }
    for (const entry of data.suggested) {
      // バックエンドの suggested は strong 候補も含む重複リスト。先に登録した
      // strong を上書きすると採用ログの strength が常に suggested に劣化するため、
      // 未登録の key/value のみ追加する。
      const group = (map[entry.key] ??= {});
      if (!(entry.value in group)) {
        group[entry.value] = { strength: 'suggested', score: entry.score };
      }
    }
    return map;
  }, [recommendationsQuery.data]);

  const { logRecommendationEvent } = useRecommendationEvents();

  // 属性ドロップダウンの推奨採用ログ (計画書§8 P4)。setAttribute を1箇所ラップし、
  // 選択された value がそのグループの推奨 (strong/suggested) に含まれる場合のみ
  // 'adopted' を記録する。推奨外の選択 (=ユーザーの自由選択) はノイズになるため記録しない。
  // ネストしたグループ (person 配下等) は最終的にオブジェクト値で setAttribute が呼ばれる
  // ため value が string にならず自然にスキップされる (意思決定点のみを狙った簡易実装)。
  const handleAttributeChange = useCallback((key: string, value: unknown) => {
    setAttribute(key, value);
    if (typeof value === 'string') {
      const matched = recommendationEntryByGroupValue[key]?.[value];
      if (matched) {
        logRecommendationEvent({
          subject_type: recommendationSubject?.subject_type ?? null,
          subject_id: recommendationSubject?.subject_id ?? null,
          surface: 'attribute_badge',
          action: 'adopted',
          group_key: key,
          option_key: value,
          strength: matched.strength,
          score: matched.score ?? null,
          profile_status: recommendationsQuery.data?.status ?? null,
        });
      }
    }
  }, [setAttribute, recommendationEntryByGroupValue, recommendationSubject, recommendationsQuery.data, logRecommendationEvent]);

  return {
    recommendationSubject,
    recommendationsQuery,
    gachaMode,
    gachaTooltip,
    gachaLabel,
    recommendedKeysByGroup,
    handleAttributeChange,
    logRecommendationEvent,
  };
}
